import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { HiPlus, HiSearch, HiPencil, HiTrash } from 'react-icons/hi';
import { Button, Input, Select, Card, CardHeader, Table, Pagination, Modal } from '../components/ui';
import { listPartners, createPartner, updatePartner, deletePartner, getLinkablePartnerUsers } from '../services/partners.api';
import { listOwners } from '../services/owners.api';
import { useAuthStore } from '../store/authStore';
import { formatDate } from '../utils/helpers';
import { getErrorMessage } from '../services/api';
import type { Partner } from '../types';

export function Partners() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'ADMIN';
  const canManage = user?.role === 'ADMIN' || user?.role === 'OWNER';

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [userId, setUserId] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['partners', { page, search }],
    queryFn: () => listPartners({ page, search }),
  });

  // Selectable users (role Partner, not yet linked) — a partner is created from one of these.
  const { data: linkableUsers } = useQuery({
    queryKey: ['linkablePartnerUsers'],
    queryFn: getLinkablePartnerUsers,
    enabled: isCreateOpen && canManage,
  });

  // Owners dropdown (admin assigns a partner to an owner)
  const { data: ownersData } = useQuery({
    queryKey: ['owners', 'dropdown'],
    queryFn: () => listOwners({ limit: 100 }),
    enabled: (isCreateOpen || isEditOpen) && isAdmin,
  });

  const createMutation = useMutation({
    mutationFn: () => createPartner({ userId, name, phone, address, ownerId: ownerId || undefined }),
    onSuccess: () => {
      toast.success('Partner created successfully');
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      queryClient.invalidateQueries({ queryKey: ['linkablePartnerUsers'] });
      setIsCreateOpen(false);
      resetForm();
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const updateMutation = useMutation({
    mutationFn: () => updatePartner(selectedPartner!.id, { name, phone, address, ownerId: ownerId || null }),
    onSuccess: () => {
      toast.success('Partner updated successfully');
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      setIsEditOpen(false);
      setSelectedPartner(null);
      resetForm();
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deletePartner(selectedPartner!.id),
    onSuccess: () => {
      toast.success('Partner deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      setIsDeleteOpen(false);
      setSelectedPartner(null);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const resetForm = () => {
    setName('');
    setEmail('');
    setPhone('');
    setAddress('');
    setOwnerId('');
    setUserId('');
  };

  const openEditModal = (partner: Partner, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedPartner(partner);
    setName(partner.name);
    setEmail(partner.email);
    setPhone(partner.phone || '');
    setAddress(partner.address || '');
    // List items carry the nested `owner` object (not a flat ownerId) — read both so the
    // currently assigned owner stays selected and isn't accidentally cleared on save.
    setOwnerId(partner.owner?.id || partner.ownerId || '');
    setIsEditOpen(true);
  };

  const openDeleteModal = (partner: Partner, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedPartner(partner);
    setIsDeleteOpen(true);
  };

  const columns = [
    { key: 'name', header: 'Name' },
    { key: 'email', header: 'Email' },
    { key: 'owner', header: 'Owner', render: (p: Partner) => p.owner?.name || '-' },
    { key: 'phone', header: 'Phone', render: (p: Partner) => p.phone || '-' },
    {
      key: 'customers',
      header: 'Customers',
      render: (p: Partner) => p._count?.customers ?? 0,
    },
    {
      key: 'users',
      header: 'Users',
      render: (p: Partner) => p._count?.users ?? 0,
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (p: Partner) => formatDate(p.createdAt),
    },
    ...(canManage
      ? [
          {
            key: 'actions',
            header: 'Actions',
            render: (p: Partner) => (
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={(e) => openEditModal(p, e)}
                >
                  <HiPencil className="w-4 h-4" />
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={(e) => openDeleteModal(p, e)}
                >
                  <HiTrash className="w-4 h-4" />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <CardHeader
        title="Partners"
        description="Manage partner organizations"
        action={
          canManage && (
            <Button onClick={() => setIsCreateOpen(true)}>
              <HiPlus className="w-4 h-4 mr-2" />
              New Partner
            </Button>
          )
        }
      />

      <Card className="mb-6" padding={false}>
        <div className="p-4 border-b border-gray-200">
          <div className="relative max-w-md">
            <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Search partners..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <Table
          columns={columns}
          data={data?.data || []}
          keyExtractor={(partner) => partner.id}
          onRowClick={(partner) => navigate(`/partners/${partner.id}`)}
          isLoading={isLoading}
          emptyMessage="No partners found"
        />

        {data && data.totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={data.totalPages}
            total={data.total}
            limit={data.limit}
            onPageChange={setPage}
          />
        )}
      </Card>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Create New Partner"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate();
          }}
          className="space-y-4"
        >
          {linkableUsers && linkableUsers.length === 0 ? (
            <div className="text-sm bg-amber-50 text-amber-700 rounded-lg px-3 py-2">
              Kein verknüpfbarer Benutzer vorhanden. Bitte zuerst unter <strong>Users</strong> einen Benutzer mit
              Rolle <strong>Partner</strong> anlegen (E-Mail + Rolle), dann hier auswählen.
            </div>
          ) : (
            <Select
              label="Benutzer (Rolle Partner)"
              options={[
                { value: '', label: 'Benutzer auswählen…' },
                ...(linkableUsers?.map((u) => ({
                  value: u.id,
                  label: `${u.email} (${u.firstName} ${u.lastName})`,
                })) || []),
              ]}
              value={userId}
              onChange={(e) => {
                setUserId(e.target.value);
                const sel = linkableUsers?.find((u) => u.id === e.target.value);
                setEmail(sel?.email || '');
                if (sel && !name) setName(`${sel.firstName} ${sel.lastName}`.trim());
              }}
              required
            />
          )}
          {email && <p className="-mt-2 text-xs text-gray-500">E-Mail: {email}</p>}
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <Input
            label="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <Input
            label="Address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
          {isAdmin && (
            <Select
              label="Owner"
              options={[
                { value: '', label: 'Kein Owner' },
                ...(ownersData?.data?.map((o) => ({ value: o.id, label: o.name })) || []),
              ]}
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
            />
          )}
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setIsCreateOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={createMutation.isPending} disabled={!userId}>
              Create Partner
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditOpen}
        onClose={() => {
          setIsEditOpen(false);
          setSelectedPartner(null);
          resetForm();
        }}
        title="Edit Partner"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            updateMutation.mutate();
          }}
          className="space-y-4"
        >
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <Input
            type="email"
            label="Email (vom verknüpften Benutzer)"
            value={email}
            disabled
            className="bg-gray-50 text-gray-500"
          />
          <Input
            label="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <Input
            label="Address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
          {isAdmin && (
            <Select
              label="Owner"
              options={[
                { value: '', label: 'Kein Owner' },
                ...(ownersData?.data?.map((o) => ({ value: o.id, label: o.name })) || []),
              ]}
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
            />
          )}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setIsEditOpen(false);
                setSelectedPartner(null);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" isLoading={updateMutation.isPending}>
              Save Changes
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setSelectedPartner(null);
        }}
        title="Delete Partner"
      >
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete <strong>{selectedPartner?.name}</strong>? This action
          will deactivate the partner account.
        </p>
        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={() => {
              setIsDeleteOpen(false);
              setSelectedPartner(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => deleteMutation.mutate()}
            isLoading={deleteMutation.isPending}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
}
